import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box, Text, Flex, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

/**
 * Google Sign-In button using Google Identity Services.
 * On Android (WebView), falls back to window.AndroidBridge.googleSignIn()
 * which triggers the native Credential Manager flow.
 *
 * Props:
 *  - onSuccess(credential): called with the Google ID token string
 *  - isLoading: shows spinner while processing
 *  - label: override button text (optional)
 */
function GoogleSignInButton({ onSuccess, isLoading = false, label }) {
  const { t } = useTranslation();
  const googleBtnRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Detect Android WebView with native Google Sign-In support
  const isAndroidWithNativeGoogle = typeof window !== "undefined"
    && typeof window.AndroidBridge?.googleSignIn === "function";
  // Detect Android WebView without native support (old app version)
  const isAndroidWebView = typeof window !== "undefined"
    && !!window.AndroidBridge?.isBillingAvailable?.();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const handleGoogleCallback = useCallback((response) => {
    if (response?.credential) {
      onSuccess(response.credential);
    }
  }, [onSuccess]);

  // Local loading state for Android native picker delay
  const [androidLoading, setAndroidLoading] = useState(false);

  // Android native Google Sign-In
  const handleAndroidGoogleSignIn = useCallback(() => {
    if (!window.AndroidBridge?.googleSignIn) return;
    setAndroidLoading(true);
    window.MufynGoogleAuth = {
      onResult: (credential) => {
        setAndroidLoading(false);
        if (credential) {
          onSuccess(credential);
        }
      },
      onError: (error) => {
        setAndroidLoading(false);
        console.warn("Android Google Sign-In error:", error);
      },
    };
    window.AndroidBridge.googleSignIn();
  }, [onSuccess]);

  // Track container width for responsive Google button
  useEffect(() => {
    if (isAndroidWithNativeGoogle || !clientId) return;
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.offsetWidth;
      if (w > 0) setContainerWidth(w);
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isAndroidWithNativeGoogle, clientId]);

  // Render the Google button whenever the measured width changes
  useEffect(() => {
    if (isAndroidWithNativeGoogle || !clientId || containerWidth === 0) return;

    const initGsi = () => {
      if (!window.google?.accounts?.id) {
        setTimeout(initGsi, 200);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      if (googleBtnRef.current) {
        // Clear previous render
        googleBtnRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          shape: "pill",
          width: Math.floor(containerWidth),
          text: "continue_with",
          logo_alignment: "center",
        });
      }
    };

    initGsi();
  }, [clientId, isAndroidWithNativeGoogle, handleGoogleCallback, containerWidth]);

  // Hide the button entirely if in Android WebView without native support
  // (GIS won't work in a WebView, and native bridge isn't available)
  if (isAndroidWebView && !isAndroidWithNativeGoogle) return null;

  if (!clientId && !isAndroidWithNativeGoogle) return null;

  // On Android with native Credential Manager, render a custom button
  const androidBusy = isLoading || androidLoading;

  if (isAndroidWithNativeGoogle) {
    return (
      <Box
        as="button"
        type="button"
        onClick={handleAndroidGoogleSignIn}
        disabled={androidBusy}
        w="100%"
        h="56px"
        borderRadius="16px"
        bg="white"
        border="1px solid rgba(255, 255, 255, 0.2)"
        cursor="pointer"
        transition="all 0.3s ease"
        _hover={{ bg: "gray.100", transform: "translateY(-1px)" }}
        _active={{ transform: "translateY(0)" }}
        _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
      >
        <Flex align="center" justify="center" gap={3}>
          {androidBusy ? (
            <Spinner size="sm" color="gray.700" />
          ) : (
            <>
              <GoogleIcon />
              <Text color="gray.700" fontWeight="600" fontSize="md">
                {label || t("login.google_signin", "Continue with Google")}
              </Text>
            </>
          )}
        </Flex>
      </Box>
    );
  }

  // On desktop/web, render the official Google button
  return (
    <Box ref={containerRef} w="100%" position="relative" overflow="hidden">
      {isLoading && (
        <Flex
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0,0,0,0.4)"
          borderRadius="16px"
          zIndex="10"
          align="center"
          justify="center"
        >
          <Spinner size="sm" color="white" />
        </Flex>
      )}
      <Flex justify="center" w="100%">
        <Box
          ref={googleBtnRef}
          w="100%"
          sx={{
            "& > div": { margin: "0 auto !important" },
          }}
        />
      </Flex>
    </Box>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default GoogleSignInButton;
